/**
 * First screen the player sees on launch — Singleplayer vs Cooperative.
 *
 * Singleplayer is the only enabled choice for the v0 build. Cooperative
 * shows a "Coming Soon" lock so the slot is communicated up-front. Both
 * cards share a polished gradient/glow treatment and pick up the global
 * theme accent so the visual identity carries across all intro screens.
 */

export type GameMode = 'singleplayer' | 'cooperative';

export interface ModeSelectCallbacks {
  readonly onConfirm: (mode: GameMode) => void;
}

export class ModeSelectOverlay {
  readonly element: HTMLElement;

  constructor(private readonly callbacks: ModeSelectCallbacks) {
    this.element = document.createElement('div');
    this.element.className = 'intro-overlay mode-select-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'intro-backdrop';

    const stars = document.createElement('div');
    stars.className = 'intro-stars';
    for (let i = 0; i < 70; i++) {
      const star = document.createElement('span');
      star.className = 'intro-star';
      star.style.left = `${Math.random() * 100}%`;
      star.style.top = `${Math.random() * 100}%`;
      star.style.opacity = `${(0.25 + Math.random() * 0.7).toFixed(2)}`;
      star.style.animationDelay = `${(Math.random() * -10).toFixed(2)}s`;
      star.style.animationDuration = `${(3 + Math.random() * 6).toFixed(2)}s`;
      stars.appendChild(star);
    }

    const root = document.createElement('div');
    root.className = 'intro-root';

    const header = document.createElement('header');
    header.className = 'intro-header';
    header.innerHTML = `
      <div class="intro-eyebrow">STARFALL FACTIONS</div>
      <h1 class="intro-title">Select your journey</h1>
      <p class="intro-subtitle">Single pilot or shared sector — the void rewards both.</p>
    `;

    const cards = document.createElement('div');
    cards.className = 'intro-cards';

    cards.appendChild(this.buildCard({
      mode: 'singleplayer',
      title: 'Singleplayer',
      tagline: 'Stake your own legend',
      blurb:
        'Carve your way through the sector alone. Pick a faction, paint a ship, and decide what becomes of the systems you find.',
      icon: SINGLE_ICON,
      locked: false
    }));

    cards.appendChild(this.buildCard({
      mode: 'cooperative',
      title: 'Cooperative',
      tagline: 'Hunt the void together',
      blurb:
        'Drop into the same sector with allies. Coordinate strikes, split mining runs, share the credits.',
      icon: COOP_ICON,
      locked: true
    }));

    const footer = document.createElement('footer');
    footer.className = 'intro-footer';
    footer.textContent = 'v0.5.0 · Pirate Cut';

    root.append(header, cards, footer);
    this.element.append(backdrop, stars, root);
  }

  show(): void {
    requestAnimationFrame(() => {
      this.element.classList.add('is-visible');
    });
  }

  async hideAndDispose(): Promise<void> {
    this.element.classList.remove('is-visible');
    await new Promise((resolve) => setTimeout(resolve, 320));
    this.element.remove();
  }

  private buildCard(opts: {
    mode: GameMode;
    title: string;
    tagline: string;
    blurb: string;
    icon: string;
    locked: boolean;
  }): HTMLElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'intro-card';
    card.classList.toggle('is-locked', opts.locked);
    if (opts.locked) card.disabled = true;
    card.innerHTML = `
      <div class="intro-card-icon">${opts.icon}</div>
      <div class="intro-card-tagline">${opts.tagline}</div>
      <div class="intro-card-title">${opts.title}</div>
      <p class="intro-card-blurb">${opts.blurb}</p>
      ${opts.locked ? `
        <div class="intro-card-lock">
          <div class="intro-card-lock-icon">${LOCK_ICON}</div>
          <div class="intro-card-lock-text">Coming Soon</div>
        </div>
      ` : `
        <div class="intro-card-cta">Begin →</div>
      `}
    `;
    if (!opts.locked) {
      card.addEventListener('click', () => this.callbacks.onConfirm(opts.mode));
    }
    return card;
  }
}

const SINGLE_ICON = `
<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <circle cx="32" cy="22" r="9" stroke="currentColor" stroke-width="2.4" />
  <path d="M14 54c0-9.4 8.06-17 18-17s18 7.6 18 17" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
  <circle cx="32" cy="22" r="3" fill="currentColor" opacity="0.55" />
</svg>`;

const COOP_ICON = `
<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <circle cx="22" cy="24" r="7.5" stroke="currentColor" stroke-width="2.4" />
  <circle cx="42" cy="24" r="7.5" stroke="currentColor" stroke-width="2.4" />
  <path d="M8 52c0-7.7 6.3-14 14-14 4 0 7.6 1.7 10 4.4" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
  <path d="M56 52c0-7.7-6.3-14-14-14-4 0-7.6 1.7-10 4.4" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
</svg>`;

const LOCK_ICON = `
<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="4.5" y="10" width="15" height="11" rx="2" stroke="currentColor" stroke-width="2" />
  <path d="M8 10V7a4 4 0 1 1 8 0v3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <circle cx="12" cy="15.5" r="1.6" fill="currentColor" />
</svg>`;
